import { FC } from "react";
import {
  CheckCircle,
  Clock,
  Calendar,
  User,
  Package,
  Paperclip,
  Flag,
  Edit,
  X,
} from "lucide-react";
import { capitalize } from "~/utils";
import { useGetTask } from "~/services/task";
import { TaskStatus, Priority } from "~/utils/constants";
import { Spin, Tag, Drawer } from "antd";
import { Task } from "~/types/task";
import dayjs from "dayjs";

const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return "green";
    case TaskStatus.IN_PROGRESS:
      return "blue";
    default:
      return "default";
  }
};

const getPriorityColor = (priority: Priority) => {
  switch (priority) {
    case Priority.HIGH:
      return "red";
    case Priority.MEDIUM:
      return "orange";
    case Priority.LOW:
      return "green";
    default:
      return "default";
  }
};

interface TaskDetailDrawerProps {
  taskId: string | null;
  onClose: () => void;
  onEdit?: (taskId: string) => void;
  onFinalize?: (task: Task) => void;
}

export const TaskDetailDrawer: FC<TaskDetailDrawerProps> = ({
  taskId,
  onClose,
  onEdit,
  onFinalize,
}) => {
  const { data: task, isLoading } = useGetTask(taskId || undefined);

  return (
    <Drawer
      open={!!taskId}
      onClose={onClose}
      width={480}
      title={null}
      closable={false}
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      ) : task ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-gray-400">{task.taskRef}</span>
                <Tag color={getStatusColor(task.taskStatus)}>
                  {capitalize(task.taskStatus.replace("_", " "))}
                </Tag>
                <Tag color={getPriorityColor(task.priority)}>
                  {capitalize(task.priority)}
                </Tag>
                {task.redFlag && (
                  <Tag color="red" className="text-xs">
                    Red Flag
                  </Tag>
                )}
              </div>
              <h2 className="text-lg font-semibold">{task.title}</h2>
              {task.taskType && (
                <span className="text-xs text-gray-500">
                  {capitalize(task.taskType.replace(/_/g, " "))}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>

          {/* Description */}
          <div>
            <h4 className="font-medium text-gray-700 mb-1 text-sm">
              Description
            </h4>
            <p className="text-sm text-gray-600">
              {task.description || "No description provided."}
            </p>
          </div>

          {/* Schedule */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <h4 className="font-medium text-gray-700 mb-2">Schedule</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-gray-500">Start Date</span>
                <div className="flex items-center gap-1">
                  <Calendar size={12} className="text-gray-400" />
                  {task.startDate
                    ? dayjs(task.startDate).format("MMM D, YYYY")
                    : "Not set"}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Due Date</span>
                <div className="flex items-center gap-1">
                  <Calendar size={12} className="text-gray-400" />
                  {task.dueDate
                    ? dayjs(task.dueDate).format("MMM D, YYYY")
                    : "Not set"}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Start Time</span>
                <div className="flex items-center gap-1">
                  <Clock size={12} className="text-gray-400" />
                  {task.startTime
                    ? dayjs(`2025-01-01 ${task.startTime}`).format("h:mm A")
                    : "Not set"}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Est. Duration</span>
                <div>
                  {task.duration != null ? `${task.duration}h` : "Not set"}
                </div>
              </div>
            </div>
          </div>

          {/* Assignees */}
          {task.assignees && task.assignees.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2 text-sm">
                <User size={14} className="inline mr-1" />
                Assigned Staff
              </h4>
              <div className="flex flex-wrap gap-2">
                {task.assignees.map((a) => (
                  <span
                    key={a.id}
                    className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm"
                  >
                    {a.user
                      ? `${a.user.firstName || ""} ${a.user.lastName || ""}`.trim()
                      : "Staff"}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Inventory */}
          {task.inventories && task.inventories.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2 text-sm">
                <Package size={14} className="inline mr-1" />
                Linked Inventory
              </h4>
              <div className="space-y-1">
                {task.inventories.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded text-sm"
                  >
                    <span className="font-medium text-gray-600">
                      {inv.inventoryRef}:
                    </span>
                    <span>{inv.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments / Documents */}
          {task.attachments && task.attachments.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2 text-sm">
                <Paperclip size={14} className="inline mr-1" />
                Documents ({task.attachments.length})
              </h4>
              <div className="space-y-1">
                {task.attachments.map((file: any) => (
                  <a
                    key={file.id}
                    href={file.filePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded text-sm text-blue-600 hover:bg-blue-50"
                  >
                    <Paperclip size={12} />
                    {file.fileName}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Completion details (if completed) */}
          {task.taskStatus === TaskStatus.COMPLETED && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium text-green-800">
                <CheckCircle size={14} className="inline mr-1" />
                Completed
              </h4>
              {task.actualHours !== undefined && task.actualHours !== null && (
                <div>
                  <span className="text-green-700">Actual hours: </span>
                  <span className="font-medium">{task.actualHours}h</span>
                </div>
              )}
              {task.completionNotes && (
                <div>
                  <span className="text-green-700">Notes: </span>
                  <span>{task.completionNotes}</span>
                </div>
              )}
              {task.helpers && task.helpers.length > 0 && (
                <div>
                  <span className="text-green-700">Helpers: </span>
                  <span>
                    {task.helpers
                      .map((h) =>
                        h.staff?.user
                          ? `${h.staff.user.firstName || ""} ${h.staff.user.lastName || ""}`.trim() +
                            (h.hours ? ` (${h.hours}h)` : "")
                          : "Staff"
                      )
                      .join(", ")}
                  </span>
                </div>
              )}
              {task.supplyItems && task.supplyItems.length > 0 && (
                <div>
                  <span className="text-green-700">Supplies used: </span>
                  <span>
                    {task.supplyItems
                      .map(
                        (s) =>
                          `${s.supply?.name || "Supply"} x${s.quantity}`
                      )
                      .join(", ")}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Red flag details */}
          {task.redFlag && task.redFlagNotes && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
              <h4 className="font-medium text-red-800 mb-1">
                <Flag size={14} className="inline mr-1" />
                Red Flag
              </h4>
              <p className="text-red-700">{task.redFlagNotes}</p>
            </div>
          )}

          {/* Notes */}
          {task.notes && task.notes.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2 text-sm">
                Notes ({task.notes.length})
              </h4>
              <div className="space-y-2">
                {task.notes.map((note) => (
                  <div key={note.id} className="bg-gray-50 rounded p-3 text-sm">
                    <p>{note.content}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {note.author?.firstName} {note.author?.lastName} -{" "}
                      {dayjs(note.createdAt).format("MMM D, h:mm A")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {(onEdit || (onFinalize && task.taskStatus !== TaskStatus.COMPLETED)) && (
            <div className="flex gap-2 pt-4 border-t">
              {onEdit && (
                <button
                  className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm"
                  onClick={() => {
                    onClose();
                    onEdit(task.id);
                  }}
                >
                  <Edit size={14} />
                  Edit Task
                </button>
              )}
              {onFinalize && task.taskStatus !== TaskStatus.COMPLETED && (
                <button
                  className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm"
                  onClick={() => {
                    onClose();
                    onFinalize(task);
                  }}
                >
                  <CheckCircle size={14} />
                  Finalize
                </button>
              )}
            </div>
          )}
        </div>
      ) : null}
    </Drawer>
  );
};
